"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const DynamicConfig_1 = __importDefault(require("./DynamicConfig"));
const Layer_1 = __importDefault(require("./Layer"));
const Constants_1 = require("./utils/Constants");
const Hashing_1 = require("./utils/Hashing");
const StatsigAsyncStorage_1 = __importDefault(require("./utils/StatsigAsyncStorage"));
const StatsigLocalStorage_1 = __importDefault(require("./utils/StatsigLocalStorage"));
const MAX_USER_VALUE_CACHED = 10;
class StatsigStore {
    constructor(sdkInternal) {
        this.overrides = {
            gates: {},
            configs: {},
        };
        this.sdkInternal = sdkInternal;
        this.values = {};
        this.values[this.getCurrentUserCacheKey()] = {
            feature_gates: {},
            dynamic_configs: {},
            sticky_experiments: {},
            layer_configs: {},
            time: 0,
        };
        this.stickyDeviceExperiments = {};
        this.loaded = false;
        this.loadFromLocalStorage();
    }
    async loadFromAsyncStorage() {
        this.parseCachedValues(await StatsigAsyncStorage_1.default.getItemAsync(Constants_1.INTERNAL_STORE_KEY), await StatsigAsyncStorage_1.default.getItemAsync(Constants_1.STICKY_DEVICE_EXPERIMENTS_KEY));
        this.loaded = true;
    }
    bootstrap(initializeValues) {
        var _a, _b, _c, _d;
        const key = this.sdkInternal.getCurrentUserCacheKey();
        // clients are going to assume that the SDK is bootstraped after this method runs
        // if we fail to parse, we will fall back to defaults, but we dont want to throw
        // when clients try to check gates/configs/etc after this point
        this.loaded = true;
        try {
            let userValues = (_a = this.values[key]) !== null && _a !== void 0 ? _a : {
                feature_gates: {},
                dynamic_configs: {},
                layer_configs: {},
                sticky_experiments: {},
                time: Date.now(),
            };
            userValues.feature_gates = (_b = initializeValues.feature_gates) !== null && _b !== void 0 ? _b : {};
            userValues.dynamic_configs = (_c = initializeValues.dynamic_configs) !== null && _c !== void 0 ? _c : {};
            userValues.layer_configs = (_d = initializeValues.layer_configs) !== null && _d !== void 0 ? _d : {};
            userValues.time = Date.now();
            this.values[key] = userValues;
            this.loadOverrides();
        }
        catch (_e) {
            return;
        }
    }
    loadFromLocalStorage() {
        if (StatsigAsyncStorage_1.default.asyncStorage) {
            return;
        }
        this.parseCachedValues(StatsigLocalStorage_1.default.getItem(Constants_1.INTERNAL_STORE_KEY), StatsigLocalStorage_1.default.getItem(Constants_1.STICKY_DEVICE_EXPERIMENTS_KEY));
        this.loaded = true;
    }
    isLoaded() {
        return this.loaded;
    }
    parseCachedValues(allValues, deviceExperiments) {
        try {
            this.values = allValues ? JSON.parse(allValues) : this.values;
        }
        catch (e) {
            // Cached value corrupted, remove cache
            this.removeFromStorage(Constants_1.INTERNAL_STORE_KEY);
        }
        try {
            const deviceExpParsed = deviceExperiments
                ? JSON.parse(deviceExperiments)
                : null;
            if (deviceExpParsed) {
                this.stickyDeviceExperiments = deviceExpParsed;
            }
        }
        catch (e) {
            this.removeFromStorage(Constants_1.STICKY_DEVICE_EXPERIMENTS_KEY);
        }
        this.loadOverrides();
    }
    removeFromStorage(key) {
        StatsigAsyncStorage_1.default.removeItemAsync(key);
        StatsigLocalStorage_1.default.removeItem(key);
    }
    loadOverrides() {
        const overrides = StatsigLocalStorage_1.default.getItem(Constants_1.OVERRIDES_STORE_KEY);
        if (overrides != null) {
            try {
                this.overrides = JSON.parse(overrides);
            }
            catch (e) {
                StatsigLocalStorage_1.default.removeItem(Constants_1.OVERRIDES_STORE_KEY);
            }
        }
    }
    async save(userCacheKey, jsonConfigs) {
        var _a, _b;
        // We append to the values after removing one, so must have at most MAX - 1
        const filteredValues = Object.entries(this.values)
            .sort(({ 1: a }, { 1: b }) => {
            if (a == null) {
                return 1;
            }
            if (b == null) {
                return -1;
            }
            return (b === null || b === void 0 ? void 0 : b.time) - (a === null || a === void 0 ? void 0 : a.time);
        })
            .slice(0, MAX_USER_VALUE_CACHED - 1);
        this.values = Object.fromEntries(filteredValues);
        this.values[userCacheKey] = {
            ...jsonConfigs,
            sticky_experiments: (_b = (_a = this.values[userCacheKey]) === null || _a === void 0 ? void 0 : _a.sticky_experiments) !== null && _b !== void 0 ? _b : {},
            time: Date.now(),
        };
        if (StatsigAsyncStorage_1.default.asyncStorage) {
            await StatsigAsyncStorage_1.default.setItemAsync(Constants_1.INTERNAL_STORE_KEY, JSON.stringify(this.values));
        }
        else {
            StatsigLocalStorage_1.default.setItem(Constants_1.INTERNAL_STORE_KEY, JSON.stringify(this.values));
        }
    }
    checkGate(gateName, ignoreOverrides = false) {
        var _a, _b;
        const gateNameHash = (0, Hashing_1.getHashValue)(gateName);
        let gateValue = { value: false, rule_id: '', secondary_exposures: [] };
        if (!ignoreOverrides && this.overrides.gates[gateName] != null) {
            gateValue = {
                value: this.overrides.gates[gateName],
                rule_id: 'override',
                secondary_exposures: [],
            };
        }
        else {
            gateValue =
                (_b = (_a = this.values[this.getCurrentUserCacheKey()]) === null || _a === void 0 ? void 0 : _a.feature_gates[gateNameHash]) !== null && _b !== void 0 ? _b : gateValue;
        }
        this.sdkInternal
            .getLogger()
            .logGateExposure(this.sdkInternal.getCurrentUser(), gateName, gateValue.value, gateValue.rule_id, gateValue.secondary_exposures);
        return gateValue.value === true;
    }
    getConfig(configName, ignoreOverrides = false) {
        var _a, _b;
        const configNameHash = (0, Hashing_1.getHashValue)(configName);
        let configValue = new DynamicConfig_1.default(configName);
        if (!ignoreOverrides && this.overrides.configs[configName] != null) {
            configValue = new DynamicConfig_1.default(configName, this.overrides.configs[configName], 'override');
        }
        else if (((_a = this.values[this.getCurrentUserCacheKey()]) === null || _a === void 0 ? void 0 : _a.dynamic_configs[configNameHash]) != null) {
            const rawConfigValue = (_b = this.values[this.getCurrentUserCacheKey()]) === null || _b === void 0 ? void 0 : _b.dynamic_configs[configNameHash];
            configValue = this.createDynamicConfig(configName, rawConfigValue);
        }
        this.sdkInternal
            .getLogger()
            .logConfigExposure(this.sdkInternal.getCurrentUser(), configName, configValue.getRuleID(), configValue._getSecondaryExposures());
        return configValue;
    }
    getExperiment(expName, keepDeviceValue = false, ignoreOverrides = false) {
        let exp = new DynamicConfig_1.default(expName);
        if (!ignoreOverrides && this.overrides.configs[expName] != null) {
            exp = new DynamicConfig_1.default(expName, this.overrides.configs[expName], 'override');
        }
        else {
            const latestValue = this.getLatestValue(expName, 'dynamic_configs');
            const finalValue = this.getPossiblyStickyValue(expName, latestValue, keepDeviceValue, false /* isLayer */);
            exp = this.createDynamicConfig(expName, finalValue);
        }
        this.sdkInternal
            .getLogger()
            .logConfigExposure(this.sdkInternal.getCurrentUser(), expName, exp.getRuleID(), exp._getSecondaryExposures());
        return exp;
    }
    getLayer(layerName, keepDeviceValue) {
        var _a;
        const latestValue = this.getLatestValue(layerName, 'layer_configs');
        const finalValue = this.getPossiblyStickyValue(layerName, latestValue, keepDeviceValue, true /* isLayer */);
        const config = Layer_1.default._create(layerName, finalValue === null || finalValue === void 0 ? void 0 : finalValue.value, finalValue === null || finalValue === void 0 ? void 0 : finalValue.rule_id, this.sdkInternal, finalValue === null || finalValue === void 0 ? void 0 : finalValue.secondary_exposures, finalValue === null || finalValue === void 0 ? void 0 : finalValue.undelegated_secondary_exposures, (_a = finalValue === null || finalValue === void 0 ? void 0 : finalValue.allocated_experiment_name) !== null && _a !== void 0 ? _a : '', finalValue === null || finalValue === void 0 ? void 0 : finalValue.explicit_parameters);
        return config;
    }
    overrideConfig(configName, value) {
        try {
            JSON.stringify(value);
        }
        catch (e) {
            console.warn('Failed to stringify given config override.  Dropping', e);
            return;
        }
        this.overrides.configs[configName] = value;
        this.saveOverrides();
    }
    overrideGate(gateName, value) {
        this.overrides.gates[gateName] = value;
        this.saveOverrides();
    }
    removeGateOverride(gateName) {
        if (gateName == null) {
            this.overrides.gates = {};
        }
        else {
            delete this.overrides.gates[gateName];
        }
        this.saveOverrides();
    }
    removeConfigOverride(configName) {
        if (configName == null) {
            this.overrides.configs = {};
        }
        else {
            delete this.overrides.configs[configName];
        }
        this.saveOverrides();
    }
    getAllOverrides() {
        return this.overrides;
    }
    saveOverrides() {
        try {
            StatsigLocalStorage_1.default.setItem(Constants_1.OVERRIDES_STORE_KEY, JSON.stringify(this.overrides));
        }
        catch (e) {
            console.warn('Failed to persist gate/config overrides');
        }
    }
    getCurrentUserCacheKey() {
        return this.sdkInternal.getCurrentUserCacheKey();
    }
    getLatestValue(name, topLevelKey) {
        var _a, _b, _c;
        const hash = (0, Hashing_1.getHashValue)(name);
        const userCacheKey = this.getCurrentUserCacheKey();
        const userValues = this.values[userCacheKey];
        return ((_b = (_a = userValues === null || userValues === void 0 ? void 0 : userValues[topLevelKey]) === null || _a === void 0 ? void 0 : _a[hash]) !== null && _b !== void 0 ? _b : (_c = userValues === null || userValues === void 0 ? void 0 : userValues[topLevelKey]) === null || _c === void 0 ? void 0 : _c[name]);
    }
    // Sticky Logic: https://gist.github.com/daniel-statsig/3d8dfc9bdee531cffc96901c1a06a402
    getPossiblyStickyValue(name, latestValue, keepDeviceValue, isLayer) {
        var _a;
        // We don't want sticky behavior. Clear any sticky values and return latest.
        if (!keepDeviceValue) {
            this.removeStickyValue(name);
            return latestValue;
        }
        // If there is no sticky value, save latest as sticky and return latest.
        const stickyValue = this.getStickyValue(name);
        if (!stickyValue) {
            this.attemptToSaveStickyValue(name, latestValue);
            return latestValue;
        }
        // Get the latest config value. Layers require a lookup by allocated_experiment_name.
        let latestExperimentValue = null;
        if (isLayer) {
            latestExperimentValue = this.getLatestValue((_a = stickyValue === null || stickyValue === void 0 ? void 0 : stickyValue.allocated_experiment_name) !== null && _a !== void 0 ? _a : '', 'dynamic_configs');
        }
        else {
            latestExperimentValue = latestValue;
        }
        if ((latestExperimentValue === null || latestExperimentValue === void 0 ? void 0 : latestExperimentValue.is_experiment_active) == true) {
            return stickyValue;
        }
        if ((latestValue === null || latestValue === void 0 ? void 0 : latestValue.is_experiment_active) == true) {
            this.attemptToSaveStickyValue(name, latestValue);
        }
        else {
            this.removeStickyValue(name);
        }
        return latestValue;
    }
    createDynamicConfig(name, apiConfig) {
        var _a;
        return new DynamicConfig_1.default(name, apiConfig === null || apiConfig === void 0 ? void 0 : apiConfig.value, apiConfig === null || apiConfig === void 0 ? void 0 : apiConfig.rule_id, apiConfig === null || apiConfig === void 0 ? void 0 : apiConfig.secondary_exposures, (_a = apiConfig === null || apiConfig === void 0 ? void 0 : apiConfig.allocated_experiment_name) !== null && _a !== void 0 ? _a : '');
    }
    getStickyValue(name) {
        var _a;
        const key = (0, Hashing_1.getHashValue)(name);
        const userCacheKey = this.getCurrentUserCacheKey();
        const userValues = this.values[userCacheKey];
        return ((_a = userValues === null || userValues === void 0 ? void 0 : userValues.sticky_experiments[key]) !== null && _a !== void 0 ? _a : this.stickyDeviceExperiments[key]);
    }
    attemptToSaveStickyValue(name, config) {
        if (!config) {
            return;
        }
        if (!config.is_user_in_experiment || !config.is_experiment_active) {
            return;
        }
        const userCacheKey = this.getCurrentUserCacheKey();
        const key = (0, Hashing_1.getHashValue)(name);
        const userValues = this.values[userCacheKey];
        if (config.is_device_based === true) {
            // save sticky values in memory
            this.stickyDeviceExperiments[key] = config;
        }
        else if (userValues) {
            userValues.sticky_experiments[key] = config;
        }
        // also save to persistent storage
        this.saveStickyValuesToStorage();
    }
    removeStickyValue(name) {
        var _a;
        const userCacheKey = this.getCurrentUserCacheKey();
        const key = (0, Hashing_1.getHashValue)(name);
        (_a = this.values[userCacheKey]) === null || _a === void 0 ? true : delete _a.sticky_experiments[key];
        delete this.stickyDeviceExperiments[key];
        this.saveStickyValuesToStorage();
    }
    saveStickyValuesToStorage() {
        if (StatsigAsyncStorage_1.default.asyncStorage) {
            StatsigAsyncStorage_1.default.setItemAsync(Constants_1.INTERNAL_STORE_KEY, JSON.stringify(this.values));
            StatsigAsyncStorage_1.default.setItemAsync(Constants_1.STICKY_DEVICE_EXPERIMENTS_KEY, JSON.stringify(this.stickyDeviceExperiments));
        }
        else {
            StatsigLocalStorage_1.default.setItem(Constants_1.INTERNAL_STORE_KEY, JSON.stringify(this.values));
            StatsigLocalStorage_1.default.setItem(Constants_1.STICKY_DEVICE_EXPERIMENTS_KEY, JSON.stringify(this.stickyDeviceExperiments));
        }
    }
}
exports.default = StatsigStore;
